import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormControl } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import Label from './Label';
import { Validator } from './ValidationResult';
import { makeLabel } from '../utils/generalUtils';

/**
 * Single line password input.
 * @component
 */
const PasswordInput = forwardRef(({value, live = false, onChange, hideLabel = false, placeholder, required = false, disabled = false, readonly = false, href, ...props}, ref) => {
    const [text, setText] = useState(value ?? '');
    const [validity, setValidity] = useState();
    const ctrlRef = useRef();

    useImperativeHandle(ref, () => ctrlRef.current);

    useEffect(() => {
        setText(value ?? '')
        setValidity();
    }, [value]);

    const handleUpdate = useCallback(newText => {
        const valid = ctrlRef.current?.checkValidity();
        setValidity(valid);
        if ((newText ?? '') !== (value ?? '') && valid) {    
            onChange(newText);
        }
    }, [value, onChange]);

    const handleChange = useCallback(e => {
        const newText = e.target.value;
        setText(newText);
        if (live) {
            handleUpdate(newText)
        }
    }, [live, handleUpdate])

    const textInput = <FormControl ref={ctrlRef} isInvalid={validity === false} type="password" value={text} placeholder={makeLabel(placeholder, required)} required={required} onChange={handleChange} onBlur={() => handleUpdate(text)} disabled={disabled} onInvalid={() => setValidity(false)} {...props}></FormControl>

    return readonly ? <Label hideLabel={hideLabel} placeholder={placeholder} text={text} href={href}></Label> : <FloatingLabel hideLabel={hideLabel} placeholder={placeholder ?? ''} required={required} value={text} href={href}>
        <Validator value={value} ref={ctrlRef} onValidation={setValidity}>
        {textInput}</Validator>
        </FloatingLabel>
})

PasswordInput.propTypes = {
    /**
     * The text to be displayed by the control
     */
    value: PropTypes.string,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    
    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default PasswordInput;